import { APP_CONFIG } from '@/config';
import type { MfaChallengeContext } from '@/features/auth/types/mfa.types';

type LocationCoords = {
  lat: number;
  lng: number;
};

type TempTokenState = {
  tempToken: string;
  expiresAt: string;
  isExpired: boolean;
};

const STORAGE_KEYS = {
  FIREBASE_TOKEN: 'firebase_token',
  TEMP_TOKEN: 'temp_token',
  EXPIRES_AT: 'expires_at',
  SETUP_TOKEN: 'setup_token',
  /** Challenge OTP (token devuelto por `mfa-send`); distinto del token MFA post-validación usado en headers */
  MFA_CHALLENGE: 'mfa_challenge',
  MFA_TOKEN: 'mfa_token',
  MFA_EXPIRES_AT: 'mfa_expires_at',
  // Keep these aligned with APP_CONFIG.session keys
  ACCESS_TOKEN: 'hb_auth_token',
  REFRESH_TOKEN: 'hb_refresh_token',
  LOCATION: 'user_location',
  USER: 'user',
} as const;

const SESSION_STORAGE_KEYS = new Set<string>([
  STORAGE_KEYS.FIREBASE_TOKEN,
  STORAGE_KEYS.TEMP_TOKEN,
  STORAGE_KEYS.EXPIRES_AT,
  STORAGE_KEYS.SETUP_TOKEN,
  STORAGE_KEYS.MFA_CHALLENGE,
  STORAGE_KEYS.MFA_TOKEN,
  STORAGE_KEYS.MFA_EXPIRES_AT,
  STORAGE_KEYS.LOCATION,
  STORAGE_KEYS.USER,
]);

function storageForKey(key: string): Storage {
  // Access/refresh tokens must survive page refreshes
  return SESSION_STORAGE_KEYS.has(key) ? sessionStorage : localStorage;
}

function getOrThrow(key: string): string {
  const value = storageForKey(key).getItem(key);
  if (!value) {
    throw new Error(`Missing sessionStorage key: ${key}`);
  }
  return value;
}

function getOptional(key: string): string | null {
  return storageForKey(key).getItem(key);
}

export const tokenStorage = {
  // 🔐 Firebase JWT
  setFirebaseToken(token: string) {
    sessionStorage.setItem(STORAGE_KEYS.FIREBASE_TOKEN, token);
  },
  getFirebaseToken(): string {
    return getOrThrow(STORAGE_KEYS.FIREBASE_TOKEN);
  },
  getFirebaseTokenOptional(): string | null {
    return getOptional(STORAGE_KEYS.FIREBASE_TOKEN);
  },

  // 👤 User
  setUser(data: string) {
    sessionStorage.setItem(STORAGE_KEYS.USER, data);
  },
  getUser(): string {
    return getOrThrow(STORAGE_KEYS.USER);
  },

  // ⏳ Temp token (pre-login)
  setTempToken(token: string) {
    sessionStorage.setItem(STORAGE_KEYS.TEMP_TOKEN, token);
  },
  getTempToken(): TempTokenState {
    const tempToken = getOrThrow(STORAGE_KEYS.TEMP_TOKEN);
    const expiresAt = getOrThrow(STORAGE_KEYS.EXPIRES_AT);

    return {
      tempToken,
      expiresAt,
      isExpired: Date.now() > Number(expiresAt),
    };
  },

  // ⏳ Temp token expiration
  setExpiresAt(time: string) {
    sessionStorage.setItem(STORAGE_KEYS.EXPIRES_AT, time);
  },
  getExpiresAt(): string {
    return getOrThrow(STORAGE_KEYS.EXPIRES_AT);
  },

  // 🔑 MFA setup token
  setSetupToken(token: string) {
    sessionStorage.setItem(STORAGE_KEYS.SETUP_TOKEN, token);
  },
  getSetupToken(): string {
    return getOrThrow(STORAGE_KEYS.SETUP_TOKEN);
  },
  /** Token de `mfa-setup`; si existe (y no hay challenge de `mfa-send`), el OTP va a `mfa-validate`. */
  getSetupTokenOptional(): string | null {
    return getOptional(STORAGE_KEYS.SETUP_TOKEN);
  },
  clearSetupToken() {
    sessionStorage.removeItem(STORAGE_KEYS.SETUP_TOKEN);
  },

  /** Challenge MFA (`mfa-send`) antes de introducir OTP en flujos como login */
  setMfaChallengeContext(ctx: MfaChallengeContext) {
    sessionStorage.setItem(STORAGE_KEYS.MFA_CHALLENGE, JSON.stringify(ctx));
  },
  getMfaChallengeContextOptional(): MfaChallengeContext | null {
    const raw = getOptional(STORAGE_KEYS.MFA_CHALLENGE);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as MfaChallengeContext;
    } catch {
      return null;
    }
  },
  clearMfaChallengeContext() {
    sessionStorage.removeItem(STORAGE_KEYS.MFA_CHALLENGE);
  },

  // ⏳ MFA token expiration
  setMfaExpiresAt(time: string) {
    sessionStorage.setItem(STORAGE_KEYS.MFA_EXPIRES_AT, time);
  },
  getMfaExpiresAt(): string {
    return getOrThrow(STORAGE_KEYS.MFA_EXPIRES_AT);
  },

  // 🔑 MFA verification token
  setMfaToken(token: string) {
    sessionStorage.setItem(STORAGE_KEYS.MFA_TOKEN, token);
  },
  getMfaToken(): string {
    return getOrThrow(STORAGE_KEYS.MFA_TOKEN);
  },
  getMfaTokenOptional(): string | null {
    const value = getOptional(STORAGE_KEYS.MFA_TOKEN);
    if (!value || value === 'undefined' || value === 'null') return null;
    return value;
  },

  // 📍 Location (obligatoria)
  setLocation(coords: LocationCoords) {
    sessionStorage.setItem(
      STORAGE_KEYS.LOCATION,
      JSON.stringify(coords)
    );
  },
  getLocation(): LocationCoords {
    const value = getOrThrow(STORAGE_KEYS.LOCATION);
    return JSON.parse(value) as LocationCoords;
  },

  // 🔑 Access token
  setAccessToken(token: string) {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
  },
  getAccessToken(): string {
    return getOrThrow(STORAGE_KEYS.ACCESS_TOKEN);
  },
  getAccessTokenOptional(): string | null {
    return getOptional(STORAGE_KEYS.ACCESS_TOKEN);
  },

  // 🔄 Refresh token
  setRefreshToken(token: string) {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
  },
  getRefreshToken(): string {
    return getOrThrow(STORAGE_KEYS.REFRESH_TOKEN);
  },
  getRefreshTokenOptional(): string | null {
    return getOptional(STORAGE_KEYS.REFRESH_TOKEN);
  },

  /** Access token expiry (ms epoch) from BFF `expires_in` */
  setAccessExpiresAt(expiresInSeconds: number) {
    const expiresAtMs = Date.now() + expiresInSeconds * 1000;
    localStorage.setItem(APP_CONFIG.session.expirationKey, String(expiresAtMs));
  },
  getAccessExpiresAtOptional(): number | null {
    const raw = localStorage.getItem(APP_CONFIG.session.expirationKey);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  },

  // 🧹 Limpieza total
  clearAll() {
    Object.values(STORAGE_KEYS).forEach((key) => {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    });
  },

  /**
   * Clears the session (tokens, MFA state, user) but keeps the device
   * location: the login screen stores it before signing in, and pre-login and
   * login send it, so wiping it when a new sign-in starts left the request
   * without location.
   */
  clearSession() {
    Object.values(STORAGE_KEYS)
      .filter((key) => key !== STORAGE_KEYS.LOCATION)
      .forEach((key) => {
        sessionStorage.removeItem(key);
        localStorage.removeItem(key);
      });
  },

  /**
   * Clears only what belongs to this tab (Firebase token, pre-login and MFA
   * state) and keeps the shared tokens, which another tab may have just
   * written for the session that now owns the browser.
   */
  clearTabSession() {
    SESSION_STORAGE_KEYS.forEach((key) => {
      if (key !== STORAGE_KEYS.LOCATION) sessionStorage.removeItem(key);
    });
  },

  /** Whether this tab is in the middle of its own sign-in. */
  hasPendingSignIn(): boolean {
    return !!sessionStorage.getItem(STORAGE_KEYS.FIREBASE_TOKEN) || !!sessionStorage.getItem(STORAGE_KEYS.TEMP_TOKEN);
  },

  accessTokenKey: STORAGE_KEYS.ACCESS_TOKEN,
};
